import type { PopperPlacementType } from "@mui/base/Popper";
import type { SupportTicketId } from "@redotech/effect-api-shared/ids/definitions";
import type { MongoUserId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { MessageWithSupportTicketId } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { SupportTicketListItemResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/support-ticket";
import type { AssignableUser } from "@redotech/effect-api-shared/rpc/support-ticket-related-records";
import { Divider } from "@redotech/redo-web/divider";
import { Dropdown } from "@redotech/redo-web/dropdown";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  Fragment,
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAccessibleSupportUserMap } from "../hooks/use-accessible-support-users";
import { useHoverCloseDelay } from "../hooks/use-hover-close-delay";
import { useSupportTicketMessages } from "../hooks/use-support-ticket-data";
import {
  AssigneeBadge,
  AssigneeSummaryBadge,
  ChannelBadge,
  PriorityBadge,
  SentimentBadge,
  StatusBadge,
} from "./badges/support-ticket-badges";
import * as styles from "./support-ticket-conversation-preview.module.css";

const HOVER_DELAY_MS = 700;
const CLOSE_DELAY_MS = 150;

function formatSenderLabel(
  message: MessageWithSupportTicketId,
  userMap: Map<MongoUserId, AssignableUser>,
  customerName: string | null,
): string {
  switch (message.senderType) {
    case "customer":
      return customerName ?? "Customer";
    case "merchant": {
      const senderUserId = message.senderMongoUserId;
      if (senderUserId) {
        const user = userMap.get(senderUserId);
        if (user?.name) {
          return user.name;
        }
      }
      return "Agent";
    }
    case "ai":
      return "AI";
    case "auto_reply":
      return "Auto-reply";
    case "system":
      return "System";
    case "chat_flow":
      return "Chat flow";
  }
}

const MessagePreviewItem = memo(function MessagePreviewItem({
  message,
  senderLabel,
}: {
  message: MessageWithSupportTicketId;
  senderLabel: string;
}) {
  const isFromCustomer = message.senderType === "customer";
  const content = message.content;

  return (
    <Flex dir="column" gap="xxs" py="sm">
      <Text
        fontSize="xs"
        fontWeight="medium"
        overflow="hidden"
        textColor={isFromCustomer ? "primary" : "tertiary"}
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {senderLabel}
      </Text>
      <Text
        className={styles.messageContent}
        fontSize="xs"
        lineHeight="1.5"
        textColor={content ? "secondary" : "tertiary"}
      >
        {content ?? "[attachment]"}
      </Text>
    </Flex>
  );
});

export const SupportTicketConversationPreviewCard = memo(
  function SupportTicketConversationPreviewCard({
    supportTicket,
    supportTicketId,
  }: {
    supportTicket: SupportTicketListItemResponse;
    supportTicketId: SupportTicketId;
  }) {
    const { data: messages, isLoading } =
      useSupportTicketMessages(supportTicketId);

    const userMap = useAccessibleSupportUserMap();
    const customerName = supportTicket.customer?.displayName ?? null;
    const assigneeName = supportTicket.mongoAssigneeId
      ? (userMap.get(supportTicket.mongoAssigneeId)?.name ?? null)
      : null;
    const sentiment = supportTicket.customerSentiment;
    const assigneeSummary = supportTicket.assigneeSummary;

    return (
      <Flex dir="column" gap="sm" p="md" w="sm">
        <Flex align="center" gap="sm" justify="space-between">
          <Text
            fontSize="xs"
            fontWeight="semibold"
            overflow="hidden"
            textColor="primary"
            textOverflow="ellipsis"
            whiteSpace="nowrap"
          >
            {customerName ?? "Customer"}
          </Text>
          {supportTicket.ticketNumber !== null && (
            <Text fontSize="xs" textColor="tertiary" whiteSpace="nowrap">
              #{supportTicket.ticketNumber}
            </Text>
          )}
        </Flex>

        <Flex align="center" gap="xs" wrap="wrap">
          <StatusBadge size="sm" status={supportTicket.status} />
          {supportTicket.priority === "URGENT" && (
            <PriorityBadge priority={supportTicket.priority} size="sm" />
          )}
          {supportTicket.channels.map((channel) => (
            <ChannelBadge channel={channel} key={channel} size="sm" />
          ))}
          {sentiment && sentiment !== "NEUTRAL" && (
            <SentimentBadge sentiment={sentiment} size="sm" />
          )}
          {assigneeSummary && (
            <AssigneeSummaryBadge assigneeSummary={assigneeSummary} size="sm" />
          )}
          {assigneeName && <AssigneeBadge name={assigneeName} size="sm" />}
        </Flex>

        <Flex
          borderColor="secondary"
          borderStyle="solid"
          borderTopWidth="1px"
        />

        {isLoading ? (
          <Flex dir="column" gap="sm">
            {[
              styles.shimmerW100,
              styles.shimmerW80,
              styles.shimmerW90,
              styles.shimmerW70,
              styles.shimmerW85,
            ].map((widthClass, i) => (
              <div
                className={`${styles.loadingShimmer} ${widthClass}`}
                key={i}
              />
            ))}
          </Flex>
        ) : messages.length === 0 ? (
          <Text fontSize="xs" fontStyle="italic" textColor="tertiary">
            No messages yet
          </Text>
        ) : (
          <Flex className={styles.messagesScroll} dir="column" gap="none">
            {messages.map((message, index) => (
              <Fragment key={message.id}>
                {index > 0 && <Divider />}
                <MessagePreviewItem
                  message={message}
                  senderLabel={formatSenderLabel(
                    message,
                    userMap,
                    customerName,
                  )}
                />
              </Fragment>
            ))}
          </Flex>
        )}
      </Flex>
    );
  },
);

export const SupportTicketHoverPreviewWrapper = memo(
  function SupportTicketHoverPreviewWrapper({
    children,
    className,
    placement = "right-start",
    supportTicket,
  }: {
    children: ReactNode;
    className?: string;
    placement?: PopperPlacementType;
    supportTicket: SupportTicketListItemResponse;
  }) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);
    const openTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const handleClose = useCallback(() => setIsOpen(false), []);
    const { cancelClose, scheduleClose } = useHoverCloseDelay(
      handleClose,
      CLOSE_DELAY_MS,
    );

    useEffect(() => {
      return () => {
        if (openTimerRef.current) {
          clearTimeout(openTimerRef.current);
        }
      };
    }, []);

    const handleMouseEnter = useCallback(() => {
      cancelClose();
      setAnchorEl(wrapperRef.current);
      openTimerRef.current = setTimeout(() => {
        setIsOpen(true);
      }, HOVER_DELAY_MS);
    }, [cancelClose]);

    const handleMouseLeave = useCallback(() => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
      scheduleClose();
    }, [scheduleClose]);

    return (
      <div
        className={className}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={wrapperRef}
      >
        {children}
        <Dropdown
          anchor={anchorEl}
          fitToAnchor={false}
          flexProps={{
            p: "none",
            onMouseEnter: cancelClose,
            onMouseLeave: scheduleClose,
          }}
          open={isOpen}
          placement={placement}
        >
          <SupportTicketConversationPreviewCard
            supportTicket={supportTicket}
            supportTicketId={supportTicket.id as SupportTicketId}
          />
        </Dropdown>
      </div>
    );
  },
);
